import { useEffect } from 'react';

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const values = [
  {
    icon: 'ri-shield-check-line',
    title: 'Food Safety First',
    description:
      'Every link in our supply chain is designed around rigorous food safety standards. From loading dock to final delivery, temperature integrity and hygiene compliance are non-negotiable.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Sustainable Operations',
    description:
      'We continuously invest in cleaner technologies and optimized routing to reduce our environmental footprint while maintaining the highest service levels for our clients.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Client Partnership',
    description:
      'We treat every client as a long-term partner. Dedicated account managers, transparent reporting, and flexible service agreements are the foundation of our relationships.',
  },
  {
    icon: 'ri-lightbulb-line',
    title: 'Innovation & Technology',
    description:
      'Real-time tracking, automated temperature logging, and integrated WMS platforms give our clients full visibility and control over their cold-chain inventory at all times.',
  },
];

const stats = [
  { icon: 'ri-truck-line', value: '120+', label: 'Refrigerated Vehicles' },
  { icon: 'ri-building-2-line', value: '50,000+', label: 'sq.m Cold Storage' },
  { icon: 'ri-map-2-line', value: '13', label: 'Provinces & Territories' },
  { icon: 'ri-group-line', value: '400+', label: 'Active Partners' },
];

export default function AboutModal({ isOpen, onClose }: AboutModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header image */}
        <div className="relative h-56 md:h-72 rounded-t-2xl overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/ec02c568ca53b8b1f3dade959beead50.jpg"
            alt="McALLISTER DELIGHT — Cold Chain Logistics"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-[#0F172A]/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <p className="text-[#DC2626] text-xs font-bold uppercase tracking-widest mb-2">
              WHO WE ARE
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              About McALLISTER DELIGHT
            </h2>
          </div>
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-8 md:p-10">
          {/* Mission */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-[#1A365D] mb-4">Our Mission</h3>
            <p className="text-gray-600 leading-relaxed text-base mb-4">
              McALLISTER DELIGHT is a Canadian cold-chain logistics company specialising in the
              end-to-end movement and storage of temperature-sensitive food products. Our mission
              is to connect food producers, processors, and retailers across Canada through a
              seamlessly integrated logistics network — one that maintains product quality from
              source to shelf.
            </p>
            <p className="text-gray-600 leading-relaxed text-base">
              We operate a full-service cold-chain ecosystem: refrigerated road transport, multi-zone
              cold storage warehousing, Canada-wide distribution, and cross-docking — all built on a
              foundation of food safety compliance, real-time technology, and an unwavering
              commitment to on-time performance.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {stats.map((s, i) => (
              <div
                key={i}
                className="bg-gray-50 rounded-xl p-5 text-center"
              >
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-[#DC2626]/10 rounded-full">
                  <i className={`${s.icon} text-[#DC2626] text-xl`}></i>
                </div>
                <div className="text-2xl font-bold text-[#1A365D] mb-1">{s.value}</div>
                <div className="text-xs text-gray-500 font-medium">{s.label}</div>
              </div>
            ))}
          </div>

          {/* What We Do */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-[#1A365D] mb-4">What We Do</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                {
                  icon: 'ri-truck-line',
                  title: 'Refrigerated Transport',
                  desc: 'A dedicated fleet of temperature-controlled vehicles serving all Canadian provinces, operating around the clock with automated cold-chain monitoring.',
                },
                {
                  icon: 'ri-building-line',
                  title: 'Cold Storage Warehousing',
                  desc: 'Multi-zone facilities ranging from ambient to deep-freeze, equipped with advanced WMS for real-time inventory visibility and HACCP-compliant handling.',
                },
                {
                  icon: 'ri-map-pin-line',
                  title: 'Canada-Wide Distribution',
                  desc: 'Route-optimised distribution covering every province and territory, including last-mile urban delivery and EDI integration with major retail networks.',
                },
                {
                  icon: 'ri-exchange-line',
                  title: 'Cross-Docking & Consolidation',
                  desc: 'Strategically positioned cross-docking hubs enable rapid freight consolidation and transfer, reducing transit times and improving overall supply chain efficiency.',
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start space-x-4 p-4 rounded-xl bg-gray-50">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#DC2626] to-[#F97316] rounded-full">
                    <i className={`${item.icon} text-white text-base`}></i>
                  </div>
                  <div>
                    <div className="font-semibold text-[#1A365D] text-sm mb-1">{item.title}</div>
                    <div className="text-gray-500 text-xs leading-relaxed">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Values */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-[#1A365D] mb-6">Our Core Values</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {values.map((v, i) => (
                <div key={i} className="flex items-start space-x-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#1A365D]/5 rounded-lg">
                    <i className={`${v.icon} text-[#1A365D] text-lg`}></i>
                  </div>
                  <div>
                    <div className="font-semibold text-[#1A365D] text-sm mb-1">{v.title}</div>
                    <div className="text-gray-500 text-xs leading-relaxed">{v.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Certifications strip */}
          <div className="border-t border-gray-100 pt-7">
            <p className="text-xs text-gray-400 uppercase font-bold tracking-widest mb-4">
              Compliance &amp; Certifications
            </p>
            <div className="flex flex-wrap gap-3">
              {['HACCP', 'SQF Level 2', 'ISO 22000', 'CFIA Registered', 'Transport Canada', 'LEED Silver'].map(
                (cert) => (
                  <span
                    key={cert}
                    className="px-4 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold"
                  >
                    {cert}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
